/**
 * Localized section headings for CountryLandingPageTemplate
 * Ensures all H2/H3 headings contain keyword-rich localized text
 * instead of hardcoded English — critical for international SEO ranking.
 */

export interface CountryTemplateLabels {
  servicesTitle: (country: string) => string;
  servicesSubtitle: (country: string, langs: string) => string;
  whyMoroccoTitle: (country: string) => string;
  whyMoroccoSubtitle: (country: string) => string;
  kpiTitle: (country: string) => string;
  kpiSubtitle: (country: string) => string;
  pricingTitle: (country: string) => string;
  industriesTitle: (country: string) => string;
  industriesSubtitle: (country: string) => string;
  languagesTitle: (country: string) => string;
  languagesSubtitle: (country: string) => string;
  complianceTitle: (country: string) => string;
  complianceSubtitle: (country: string) => string;
  timelineTitle: (country: string) => string;
  timelineSubtitle: string;
  faqTitle: (country: string) => string;
  relatedTitle: string;
  ctaTitle: (country: string) => string;
  ctaSubtitle: (country: string) => string;
  ctaButton: string;
  learnMoreMorocco: string;
  exploreIndustries: string;
  viewPricing: string;
}

const en: CountryTemplateLabels = {
  servicesTitle: (c) => `Call Center Services for ${c}`,
  servicesSubtitle: (c, l) => `Our dedicated teams for ${c} provide comprehensive customer support across all channels, with native ${l} speakers.`,
  whyMoroccoTitle: (c) => `Why Outsource from ${c} to Morocco?`,
  whyMoroccoSubtitle: (c) => `Morocco offers unmatched nearshore advantages for ${c}-based companies: cultural proximity, timezone alignment, multilingual talent, and 50-70% cost savings compared to local operations.`,
  kpiTitle: (c) => `Guaranteed Performance for ${c} Clients`,
  kpiSubtitle: (c) => `We contractually guarantee KPIs with SLA-backed service level agreements tailored to ${c} market expectations.`,
  pricingTitle: (c) => `Cost Comparison: ${c} vs Morocco`,
  industriesTitle: (c) => `Industries We Serve in ${c}`,
  industriesSubtitle: (c) => `We have deep domain expertise in the industries that drive the ${c} economy, with trained agents who understand sector-specific terminology and compliance requirements.`,
  languagesTitle: (c) => `Languages for ${c} Market`,
  languagesSubtitle: (c) => `Our agents are native speakers — not trained second-language speakers. We cover all the languages your ${c} customers expect.`,
  complianceTitle: (c) => `Enterprise Compliance for ${c}`,
  complianceSubtitle: (c) => `All operations serving ${c} clients are aligned with international data protection standards and local regulatory requirements.`,
  timelineTitle: (c) => `Go-Live Timeline for ${c} Projects`,
  timelineSubtitle: 'From signing to go-live in as little as 2-4 weeks. We handle recruitment, training, systems integration, and quality assurance.',
  faqTitle: (c) => `Frequently Asked Questions — Call Center for ${c}`,
  relatedTitle: 'Related Resources',
  ctaTitle: (c) => `Ready to Outsource Your ${c} Customer Support?`,
  ctaSubtitle: (c) => `Get a custom proposal tailored to ${c} market requirements. Free consultation, no commitment.`,
  ctaButton: 'Request a Free Consultation',
  learnMoreMorocco: 'Learn more about Morocco as a BPO destination',
  exploreIndustries: 'Explore all industries',
  viewPricing: 'View detailed pricing models',
};

const fr: CountryTemplateLabels = {
  servicesTitle: (c) => `Services de Centre d'Appel pour ${c}`,
  servicesSubtitle: (c, l) => `Nos équipes dédiées pour ${c} offrent un support client complet sur tous les canaux, avec des agents ${l} natifs.`,
  whyMoroccoTitle: (c) => `Pourquoi externaliser depuis ${c} vers le Maroc ?`,
  whyMoroccoSubtitle: (c) => `Le Maroc offre des avantages nearshore inégalés pour les entreprises basées en ${c} : proximité culturelle, alignement des fuseaux horaires, talents multilingues et 50-70% d'économies.`,
  kpiTitle: (c) => `Performance garantie pour les clients ${c}`,
  kpiSubtitle: (c) => `Nous garantissons contractuellement les KPIs avec des SLA adaptés aux exigences du marché ${c}.`,
  pricingTitle: (c) => `Comparaison des coûts : ${c} vs Maroc`,
  industriesTitle: (c) => `Secteurs d'activité pour ${c}`,
  industriesSubtitle: (c) => `Nous avons une expertise sectorielle approfondie dans les industries qui animent l'économie de ${c}.`,
  languagesTitle: (c) => `Langues pour le marché ${c}`,
  languagesSubtitle: (c) => `Nos agents sont des locuteurs natifs. Nous couvrons toutes les langues attendues par vos clients ${c}.`,
  complianceTitle: (c) => `Conformité pour ${c}`,
  complianceSubtitle: (c) => `Toutes les opérations pour ${c} respectent les normes internationales de protection des données et les exigences réglementaires locales.`,
  timelineTitle: (c) => `Calendrier de mise en service pour ${c}`,
  timelineSubtitle: 'De la signature au lancement en 2 à 4 semaines. Nous gérons le recrutement, la formation, l\'intégration et l\'assurance qualité.',
  faqTitle: (c) => `Questions fréquentes — Centre d'appel pour ${c}`,
  relatedTitle: 'Ressources associées',
  ctaTitle: (c) => `Prêt à externaliser votre support client ${c} ?`,
  ctaSubtitle: (c) => `Obtenez une proposition sur mesure pour le marché ${c}. Consultation gratuite, sans engagement.`,
  ctaButton: 'Demander une consultation gratuite',
  learnMoreMorocco: 'En savoir plus sur le Maroc comme destination BPO',
  exploreIndustries: 'Explorer tous les secteurs',
  viewPricing: 'Voir les tarifs détaillés',
};

const es: CountryTemplateLabels = {
  servicesTitle: (c) => `Servicios de Call Center para ${c}`,
  servicesSubtitle: (c, l) => `Nuestros equipos dedicados para ${c} ofrecen soporte integral en todos los canales, con agentes nativos de ${l}.`,
  whyMoroccoTitle: (c) => `¿Por qué externalizar desde ${c} a Marruecos?`,
  whyMoroccoSubtitle: (c) => `Marruecos ofrece ventajas nearshore inigualables para empresas de ${c}: proximidad cultural, alineación horaria, talento multilingüe y 50-70% de ahorro.`,
  kpiTitle: (c) => `Rendimiento garantizado para clientes de ${c}`,
  kpiSubtitle: (c) => `Garantizamos contractualmente los KPIs con SLAs adaptados a las exigencias del mercado ${c}.`,
  pricingTitle: (c) => `Comparación de costos: ${c} vs Marruecos`,
  industriesTitle: (c) => `Sectores que servimos en ${c}`,
  industriesSubtitle: (c) => `Tenemos profunda experiencia en las industrias que impulsan la economía de ${c}.`,
  languagesTitle: (c) => `Idiomas para el mercado ${c}`,
  languagesSubtitle: (c) => `Nuestros agentes son hablantes nativos. Cubrimos todos los idiomas que esperan sus clientes de ${c}.`,
  complianceTitle: (c) => `Cumplimiento normativo para ${c}`,
  complianceSubtitle: (c) => `Todas las operaciones para ${c} cumplen con los estándares internacionales de protección de datos.`,
  timelineTitle: (c) => `Calendario de puesta en marcha para ${c}`,
  timelineSubtitle: 'De la firma al lanzamiento en 2-4 semanas. Gestionamos reclutamiento, formación, integración y control de calidad.',
  faqTitle: (c) => `Preguntas frecuentes — Call Center para ${c}`,
  relatedTitle: 'Recursos relacionados',
  ctaTitle: (c) => `¿Listo para externalizar su soporte al cliente de ${c}?`,
  ctaSubtitle: (c) => `Obtenga una propuesta personalizada para el mercado ${c}. Consulta gratuita, sin compromiso.`,
  ctaButton: 'Solicitar una consulta gratuita',
  learnMoreMorocco: 'Más información sobre Marruecos como destino BPO',
  exploreIndustries: 'Explorar todos los sectores',
  viewPricing: 'Ver precios detallados',
};

const ar: CountryTemplateLabels = {
  servicesTitle: (c) => `خدمات مركز الاتصال لـ ${c}`,
  servicesSubtitle: (c, l) => `فرقنا المتخصصة لـ ${c} تقدم دعم عملاء شامل عبر جميع القنوات، مع متحدثين أصليين بـ ${l}.`,
  whyMoroccoTitle: (c) => `لماذا الاستعانة بمصادر خارجية من ${c} إلى المغرب؟`,
  whyMoroccoSubtitle: (c) => `يقدم المغرب مزايا nearshore لا مثيل لها لشركات ${c}: القرب الثقافي، توافق المنطقة الزمنية، الكفاءات متعددة اللغات وتوفير 50-70%.`,
  kpiTitle: (c) => `أداء مضمون لعملاء ${c}`,
  kpiSubtitle: (c) => `نضمن تعاقدياً مؤشرات الأداء مع اتفاقيات مستوى خدمة مصممة لمتطلبات سوق ${c}.`,
  pricingTitle: (c) => `مقارنة التكاليف: ${c} مقابل المغرب`,
  industriesTitle: (c) => `القطاعات التي نخدمها في ${c}`,
  industriesSubtitle: (c) => `لدينا خبرة عميقة في القطاعات التي تدفع اقتصاد ${c}.`,
  languagesTitle: (c) => `اللغات لسوق ${c}`,
  languagesSubtitle: (c) => `وكلاؤنا متحدثون أصليون. نغطي جميع اللغات التي يتوقعها عملاء ${c}.`,
  complianceTitle: (c) => `الامتثال المؤسسي لـ ${c}`,
  complianceSubtitle: (c) => `جميع العمليات لـ ${c} متوافقة مع معايير حماية البيانات الدولية.`,
  timelineTitle: (c) => `الجدول الزمني للإطلاق لمشاريع ${c}`,
  timelineSubtitle: 'من التوقيع إلى الإطلاق في 2-4 أسابيع. نتولى التوظيف والتدريب والتكامل وضمان الجودة.',
  faqTitle: (c) => `الأسئلة الشائعة — مركز اتصال لـ ${c}`,
  relatedTitle: 'موارد ذات صلة',
  ctaTitle: (c) => `هل أنت مستعد لتعهيد دعم عملاء ${c}؟`,
  ctaSubtitle: (c) => `احصل على عرض مخصص لمتطلبات سوق ${c}. استشارة مجانية، بدون التزام.`,
  ctaButton: 'طلب استشارة مجانية',
  learnMoreMorocco: 'اعرف المزيد عن المغرب كوجهة BPO',
  exploreIndustries: 'استكشف جميع القطاعات',
  viewPricing: 'عرض الأسعار المفصلة',
};

const de: CountryTemplateLabels = {
  servicesTitle: (c) => `Callcenter-Dienstleistungen für ${c}`,
  servicesSubtitle: (c, l) => `Unsere dedizierten Teams für ${c} bieten umfassenden Kundensupport über alle Kanäle mit ${l}-Muttersprachlern.`,
  whyMoroccoTitle: (c) => `Warum von ${c} nach Marokko auslagern?`,
  whyMoroccoSubtitle: (c) => `Marokko bietet unübertroffene Nearshore-Vorteile für ${c}: kulturelle Nähe, Zeitzonen-Ausrichtung, mehrsprachige Talente und 50-70% Kosteneinsparung.`,
  kpiTitle: (c) => `Garantierte Leistung für ${c}-Kunden`,
  kpiSubtitle: (c) => `Wir garantieren vertraglich KPIs mit SLA-basierten Servicelevel-Vereinbarungen für den ${c}-Markt.`,
  pricingTitle: (c) => `Kostenvergleich: ${c} vs Marokko`,
  industriesTitle: (c) => `Branchen in ${c}`,
  industriesSubtitle: (c) => `Wir haben tiefe Branchenexpertise in den Wirtschaftszweigen, die die ${c}-Wirtschaft antreiben.`,
  languagesTitle: (c) => `Sprachen für den ${c}-Markt`,
  languagesSubtitle: (c) => `Unsere Agenten sind Muttersprachler. Wir decken alle Sprachen ab, die Ihre ${c}-Kunden erwarten.`,
  complianceTitle: (c) => `Compliance für ${c}`,
  complianceSubtitle: (c) => `Alle Operationen für ${c} sind an internationale Datenschutzstandards und lokale Anforderungen angepasst.`,
  timelineTitle: (c) => `Go-Live-Zeitplan für ${c}-Projekte`,
  timelineSubtitle: 'Von der Unterzeichnung bis zum Go-Live in nur 2-4 Wochen. Wir übernehmen Rekrutierung, Schulung, Integration und Qualitätssicherung.',
  faqTitle: (c) => `Häufige Fragen — Callcenter für ${c}`,
  relatedTitle: 'Verwandte Ressourcen',
  ctaTitle: (c) => `Bereit, Ihren ${c}-Kundensupport auszulagern?`,
  ctaSubtitle: (c) => `Erhalten Sie ein maßgeschneidertes Angebot für den ${c}-Markt. Kostenlose Beratung, ohne Verpflichtung.`,
  ctaButton: 'Kostenlose Beratung anfordern',
  learnMoreMorocco: 'Erfahren Sie mehr über Marokko als BPO-Standort',
  exploreIndustries: 'Alle Branchen erkunden',
  viewPricing: 'Detaillierte Preise anzeigen',
};

const it: CountryTemplateLabels = {
  servicesTitle: (c) => `Servizi di Call Center per ${c}`,
  servicesSubtitle: (c, l) => `I nostri team dedicati per ${c} offrono supporto clienti completo su tutti i canali, con agenti madrelingua ${l}.`,
  whyMoroccoTitle: (c) => `Perché esternalizzare da ${c} al Marocco?`,
  whyMoroccoSubtitle: (c) => `Il Marocco offre vantaggi nearshore impareggiabili per le aziende di ${c}: vicinanza culturale, allineamento dei fusi orari, talenti multilingue e risparmi del 50-70%.`,
  kpiTitle: (c) => `Performance garantite per clienti ${c}`,
  kpiSubtitle: (c) => `Garantiamo contrattualmente i KPI con SLA su misura per il mercato ${c}.`,
  pricingTitle: (c) => `Confronto costi: ${c} vs Marocco`,
  industriesTitle: (c) => `Settori serviti in ${c}`,
  industriesSubtitle: (c) => `Abbiamo profonda esperienza nei settori chiave dell'economia di ${c}.`,
  languagesTitle: (c) => `Lingue per il mercato ${c}`,
  languagesSubtitle: (c) => `I nostri agenti sono madrelingua. Copriamo tutte le lingue attese dai vostri clienti ${c}.`,
  complianceTitle: (c) => `Compliance per ${c}`,
  complianceSubtitle: (c) => `Tutte le operazioni per ${c} sono allineate agli standard internazionali di protezione dati.`,
  timelineTitle: (c) => `Timeline di avvio per progetti ${c}`,
  timelineSubtitle: 'Dalla firma al go-live in sole 2-4 settimane. Gestiamo reclutamento, formazione, integrazione e quality assurance.',
  faqTitle: (c) => `Domande frequenti — Call Center per ${c}`,
  relatedTitle: 'Risorse correlate',
  ctaTitle: (c) => `Pronto a esternalizzare il supporto clienti ${c}?`,
  ctaSubtitle: (c) => `Ottieni una proposta su misura per il mercato ${c}. Consulenza gratuita, senza impegno.`,
  ctaButton: 'Richiedi una consulenza gratuita',
  learnMoreMorocco: 'Scopri di più sul Marocco come destinazione BPO',
  exploreIndustries: 'Esplora tutti i settori',
  viewPricing: 'Vedi prezzi dettagliati',
};

const pt: CountryTemplateLabels = {
  servicesTitle: (c) => `Serviços de Call Center para ${c}`,
  servicesSubtitle: (c, l) => `Nossas equipes dedicadas para ${c} oferecem suporte completo ao cliente em todos os canais, com agentes nativos de ${l}.`,
  whyMoroccoTitle: (c) => `Por que terceirizar de ${c} para o Marrocos?`,
  whyMoroccoSubtitle: (c) => `O Marrocos oferece vantagens nearshore incomparáveis para empresas de ${c}: proximidade cultural, alinhamento de fuso horário, talentos multilíngues e economia de 50-70%.`,
  kpiTitle: (c) => `Desempenho garantido para clientes ${c}`,
  kpiSubtitle: (c) => `Garantimos contratualmente os KPIs com SLAs adaptados às exigências do mercado ${c}.`,
  pricingTitle: (c) => `Comparação de custos: ${c} vs Marrocos`,
  industriesTitle: (c) => `Setores atendidos em ${c}`,
  industriesSubtitle: (c) => `Temos profunda expertise nos setores que impulsionam a economia de ${c}.`,
  languagesTitle: (c) => `Idiomas para o mercado ${c}`,
  languagesSubtitle: (c) => `Nossos agentes são falantes nativos. Cobrimos todos os idiomas esperados por seus clientes ${c}.`,
  complianceTitle: (c) => `Conformidade para ${c}`,
  complianceSubtitle: (c) => `Todas as operações para ${c} estão alinhadas com padrões internacionais de proteção de dados.`,
  timelineTitle: (c) => `Cronograma de lançamento para projetos ${c}`,
  timelineSubtitle: 'Da assinatura ao go-live em apenas 2-4 semanas. Cuidamos do recrutamento, treinamento, integração e qualidade.',
  faqTitle: (c) => `Perguntas frequentes — Call Center para ${c}`,
  relatedTitle: 'Recursos relacionados',
  ctaTitle: (c) => `Pronto para terceirizar o suporte ao cliente ${c}?`,
  ctaSubtitle: (c) => `Obtenha uma proposta personalizada para o mercado ${c}. Consulta gratuita, sem compromisso.`,
  ctaButton: 'Solicitar consulta gratuita',
  learnMoreMorocco: 'Saiba mais sobre o Marrocos como destino BPO',
  exploreIndustries: 'Explorar todos os setores',
  viewPricing: 'Ver preços detalhados',
};

const nl: CountryTemplateLabels = {
  servicesTitle: (c) => `Callcenter-diensten voor ${c}`,
  servicesSubtitle: (c, l) => `Onze teams voor ${c} bieden uitgebreide klantenondersteuning via alle kanalen, met moedertaalsprekers ${l}.`,
  whyMoroccoTitle: (c) => `Waarom uitbesteden van ${c} naar Marokko?`,
  whyMoroccoSubtitle: (c) => `Marokko biedt ongeëvenaarde nearshore-voordelen voor bedrijven uit ${c}: culturele nabijheid, tijdzone-afstemming, meertalig talent en 50-70% kostenbesparing.`,
  kpiTitle: (c) => `Gegarandeerde prestaties voor ${c}-klanten`,
  kpiSubtitle: (c) => `Wij garanderen contractueel KPI's met SLA's afgestemd op de ${c}-markt.`,
  pricingTitle: (c) => `Kostenvergelijking: ${c} vs Marokko`,
  industriesTitle: (c) => `Sectoren in ${c}`,
  industriesSubtitle: (c) => `Wij hebben diepgaande expertise in de sectoren die de ${c}-economie aandrijven.`,
  languagesTitle: (c) => `Talen voor de ${c}-markt`,
  languagesSubtitle: (c) => `Onze agenten zijn moedertaalsprekers. Wij dekken alle talen die uw ${c}-klanten verwachten.`,
  complianceTitle: (c) => `Compliance voor ${c}`,
  complianceSubtitle: (c) => `Alle operaties voor ${c} voldoen aan internationale gegevensbeschermingsnormen.`,
  timelineTitle: (c) => `Go-live tijdlijn voor ${c}-projecten`,
  timelineSubtitle: 'Van ondertekening tot go-live in slechts 2-4 weken.',
  faqTitle: (c) => `Veelgestelde vragen — Callcenter voor ${c}`,
  relatedTitle: 'Gerelateerde bronnen',
  ctaTitle: (c) => `Klaar om uw ${c}-klantenservice uit te besteden?`,
  ctaSubtitle: (c) => `Ontvang een voorstel op maat voor de ${c}-markt. Gratis advies, vrijblijvend.`,
  ctaButton: 'Gratis adviesgesprek aanvragen',
  learnMoreMorocco: 'Meer weten over Marokko als BPO-bestemming',
  exploreIndustries: 'Alle sectoren verkennen',
  viewPricing: 'Gedetailleerde prijzen bekijken',
};

const pl: CountryTemplateLabels = {
  servicesTitle: (c) => `Usługi Call Center dla ${c}`,
  servicesSubtitle: (c, l) => `Nasze zespoły dla ${c} zapewniają kompleksową obsługę klienta we wszystkich kanałach, z natywnym ${l}.`,
  whyMoroccoTitle: (c) => `Dlaczego outsourcować z ${c} do Maroka?`,
  whyMoroccoSubtitle: (c) => `Maroko oferuje niezrównane zalety nearshore dla firm z ${c}: bliskość kulturowa, strefa czasowa, wielojęzyczne talenty i 50-70% oszczędności.`,
  kpiTitle: (c) => `Gwarantowana wydajność dla klientów ${c}`,
  kpiSubtitle: (c) => `Gwarantujemy umownie KPI z SLA dostosowanymi do rynku ${c}.`,
  pricingTitle: (c) => `Porównanie kosztów: ${c} vs Maroko`,
  industriesTitle: (c) => `Branże obsługiwane w ${c}`,
  industriesSubtitle: (c) => `Mamy głęboką wiedzę branżową w sektorach napędzających gospodarkę ${c}.`,
  languagesTitle: (c) => `Języki dla rynku ${c}`,
  languagesSubtitle: (c) => `Nasi agenci są native speakerami. Pokrywamy wszystkie języki oczekiwane przez klientów ${c}.`,
  complianceTitle: (c) => `Zgodność dla ${c}`,
  complianceSubtitle: (c) => `Wszystkie operacje dla ${c} są zgodne z międzynarodowymi standardami ochrony danych.`,
  timelineTitle: (c) => `Harmonogram uruchomienia dla projektów ${c}`,
  timelineSubtitle: 'Od podpisania do uruchomienia w 2-4 tygodnie.',
  faqTitle: (c) => `Najczęściej zadawane pytania — Call Center dla ${c}`,
  relatedTitle: 'Powiązane zasoby',
  ctaTitle: (c) => `Gotowy do outsourcingu obsługi klienta ${c}?`,
  ctaSubtitle: (c) => `Uzyskaj propozycję dostosowaną do rynku ${c}. Bezpłatna konsultacja.`,
  ctaButton: 'Poproś o bezpłatną konsultację',
  learnMoreMorocco: 'Dowiedz się więcej o Maroku jako destynacji BPO',
  exploreIndustries: 'Przeglądaj wszystkie branże',
  viewPricing: 'Zobacz szczegółowe ceny',
};

const tr: CountryTemplateLabels = {
  servicesTitle: (c) => `${c} İçin Çağrı Merkezi Hizmetleri`,
  servicesSubtitle: (c, l) => `${c} için özel ekiplerimiz tüm kanallarda kapsamlı müşteri desteği sunar, anadil ${l} konuşanlarıyla.`,
  whyMoroccoTitle: (c) => `${c}'den Fas'a Neden Dış Kaynak Kullanmalı?`,
  whyMoroccoSubtitle: (c) => `Fas, ${c} şirketleri için eşsiz nearshore avantajları sunar: kültürel yakınlık, saat dilimi uyumu, çok dilli yetenekler ve %50-70 maliyet tasarrufu.`,
  kpiTitle: (c) => `${c} Müşterileri İçin Garantili Performans`,
  kpiSubtitle: (c) => `${c} pazarına özel SLA destekli KPI'ları sözleşmeyle garanti ediyoruz.`,
  pricingTitle: (c) => `Maliyet Karşılaştırması: ${c} vs Fas`,
  industriesTitle: (c) => `${c}'de Hizmet Verdiğimiz Sektörler`,
  industriesSubtitle: (c) => `${c} ekonomisini yönlendiren sektörlerde derin uzmanlığa sahibiz.`,
  languagesTitle: (c) => `${c} Pazarı İçin Diller`,
  languagesSubtitle: (c) => `Temsilcilerimiz anadil konuşanlardır. ${c} müşterilerinizin beklediği tüm dilleri kapsıyoruz.`,
  complianceTitle: (c) => `${c} İçin Uyumluluk`,
  complianceSubtitle: (c) => `${c} için tüm operasyonlar uluslararası veri koruma standartlarına uygundur.`,
  timelineTitle: (c) => `${c} Projeleri İçin Başlangıç Takvimi`,
  timelineSubtitle: 'İmzadan canlıya yalnızca 2-4 haftada.',
  faqTitle: (c) => `Sık Sorulan Sorular — ${c} Çağrı Merkezi`,
  relatedTitle: 'İlgili Kaynaklar',
  ctaTitle: (c) => `${c} Müşteri Desteğinizi Dışarıya Vermeye Hazır mısınız?`,
  ctaSubtitle: (c) => `${c} pazarına özel bir teklif alın. Ücretsiz danışmanlık.`,
  ctaButton: 'Ücretsiz danışmanlık talep edin',
  learnMoreMorocco: 'BPO destinasyonu olarak Fas hakkında daha fazla bilgi',
  exploreIndustries: 'Tüm sektörleri keşfedin',
  viewPricing: 'Detaylı fiyatları görün',
};

const sv: CountryTemplateLabels = {
  servicesTitle: (c) => `Callcentertjänster för ${c}`,
  servicesSubtitle: (c, l) => `Våra team för ${c} erbjuder omfattande kundsupport via alla kanaler, med modersmålstalare ${l}.`,
  whyMoroccoTitle: (c) => `Varför outsourca från ${c} till Marocko?`,
  whyMoroccoSubtitle: (c) => `Marocko erbjuder oöverträffade nearshore-fördelar för ${c}-företag: kulturell närhet, tidszonsanpassning och 50-70% kostnadsbesparing.`,
  kpiTitle: (c) => `Garanterad prestation för ${c}-kunder`,
  kpiSubtitle: (c) => `Vi garanterar kontraktuellt KPI:er med SLA:er anpassade till ${c}-marknaden.`,
  pricingTitle: (c) => `Kostnadsjämförelse: ${c} vs Marocko`,
  industriesTitle: (c) => `Branscher vi betjänar i ${c}`,
  industriesSubtitle: (c) => `Vi har djup branschexpertis inom sektorerna som driver ${c}-ekonomin.`,
  languagesTitle: (c) => `Språk för ${c}-marknaden`,
  languagesSubtitle: (c) => `Våra agenter är modersmålstalare. Vi täcker alla språk som era ${c}-kunder förväntar sig.`,
  complianceTitle: (c) => `Compliance för ${c}`,
  complianceSubtitle: (c) => `Alla operationer för ${c} följer internationella dataskyddsstandarder.`,
  timelineTitle: (c) => `Go-live-tidslinje för ${c}-projekt`,
  timelineSubtitle: 'Från signering till go-live på bara 2-4 veckor.',
  faqTitle: (c) => `Vanliga frågor — Callcenter för ${c}`,
  relatedTitle: 'Relaterade resurser',
  ctaTitle: (c) => `Redo att outsourca ert ${c}-kundsupport?`,
  ctaSubtitle: (c) => `Få ett skräddarsytt förslag för ${c}-marknaden. Gratis rådgivning.`,
  ctaButton: 'Begär en gratis konsultation',
  learnMoreMorocco: 'Läs mer om Marocko som BPO-destination',
  exploreIndustries: 'Utforska alla branscher',
  viewPricing: 'Visa detaljerade priser',
};

const da: CountryTemplateLabels = {
  servicesTitle: (c) => `Callcenter-tjenester for ${c}`,
  servicesSubtitle: (c, l) => `Vores teams for ${c} tilbyder omfattende kundesupport på alle kanaler med modersmålstalende ${l}.`,
  whyMoroccoTitle: (c) => `Hvorfor outsource fra ${c} til Marokko?`,
  whyMoroccoSubtitle: (c) => `Marokko tilbyder uovertruffen nearshore-fordele for ${c}-virksomheder: kulturel nærhed, tidszone og 50-70% besparelse.`,
  kpiTitle: (c) => `Garanteret performance for ${c}-kunder`,
  kpiSubtitle: (c) => `Vi garanterer kontraktligt KPI'er med SLA'er tilpasset ${c}-markedet.`,
  pricingTitle: (c) => `Omkostningssammenligning: ${c} vs Marokko`,
  industriesTitle: (c) => `Brancher vi betjener i ${c}`,
  industriesSubtitle: (c) => `Vi har dyb brancheekspertise i sektorerne der driver ${c}-økonomien.`,
  languagesTitle: (c) => `Sprog til ${c}-markedet`,
  languagesSubtitle: (c) => `Vores agenter er modersmålstalende. Vi dækker alle sprog som jeres ${c}-kunder forventer.`,
  complianceTitle: (c) => `Compliance for ${c}`,
  complianceSubtitle: (c) => `Alle operationer for ${c} overholder internationale databeskyttelsesstandarder.`,
  timelineTitle: (c) => `Go-live tidsplan for ${c}-projekter`,
  timelineSubtitle: 'Fra underskrift til go-live på kun 2-4 uger.',
  faqTitle: (c) => `Ofte stillede spørgsmål — Callcenter for ${c}`,
  relatedTitle: 'Relaterede ressourcer',
  ctaTitle: (c) => `Klar til at outsource jeres ${c}-kundesupport?`,
  ctaSubtitle: (c) => `Få et skræddersyet tilbud til ${c}-markedet. Gratis rådgivning.`,
  ctaButton: 'Anmod om gratis rådgivning',
  learnMoreMorocco: 'Læs mere om Marokko som BPO-destination',
  exploreIndustries: 'Udforsk alle brancher',
  viewPricing: 'Se detaljerede priser',
};

const ru: CountryTemplateLabels = {
  servicesTitle: (c) => `Услуги колл-центра для ${c}`,
  servicesSubtitle: (c, l) => `Наши команды для ${c} обеспечивают комплексную поддержку клиентов по всем каналам с носителями ${l}.`,
  whyMoroccoTitle: (c) => `Почему аутсорсинг из ${c} в Марокко?`,
  whyMoroccoSubtitle: (c) => `Марокко предлагает непревзойденные nearshore-преимущества для компаний ${c}: культурная близость, совпадение часовых поясов и экономия 50-70%.`,
  kpiTitle: (c) => `Гарантированная производительность для клиентов ${c}`,
  kpiSubtitle: (c) => `Мы контрактно гарантируем KPI с SLA, адаптированными к рынку ${c}.`,
  pricingTitle: (c) => `Сравнение стоимости: ${c} vs Марокко`,
  industriesTitle: (c) => `Отрасли в ${c}`,
  industriesSubtitle: (c) => `У нас глубокая экспертиза в отраслях, движущих экономику ${c}.`,
  languagesTitle: (c) => `Языки для рынка ${c}`,
  languagesSubtitle: (c) => `Наши агенты — носители языка. Мы покрываем все языки, которые ожидают ваши клиенты в ${c}.`,
  complianceTitle: (c) => `Комплаенс для ${c}`,
  complianceSubtitle: (c) => `Все операции для ${c} соответствуют международным стандартам защиты данных.`,
  timelineTitle: (c) => `Сроки запуска для проектов ${c}`,
  timelineSubtitle: 'От подписания до запуска за 2-4 недели.',
  faqTitle: (c) => `Часто задаваемые вопросы — Колл-центр для ${c}`,
  relatedTitle: 'Связанные ресурсы',
  ctaTitle: (c) => `Готовы передать на аутсорсинг поддержку клиентов ${c}?`,
  ctaSubtitle: (c) => `Получите индивидуальное предложение для рынка ${c}. Бесплатная консультация.`,
  ctaButton: 'Запросить бесплатную консультацию',
  learnMoreMorocco: 'Узнайте больше о Марокко как BPO-направлении',
  exploreIndustries: 'Просмотреть все отрасли',
  viewPricing: 'Посмотреть подробные цены',
};

const zh: CountryTemplateLabels = {
  servicesTitle: (c) => `${c}呼叫中心服务`,
  servicesSubtitle: (c, l) => `我们为${c}的专属团队提供全渠道客户支持，拥有${l}母语座席。`,
  whyMoroccoTitle: (c) => `为什么从${c}外包到摩洛哥？`,
  whyMoroccoSubtitle: (c) => `摩洛哥为${c}企业提供无与伦比的nearshore优势：文化相近、时区一致、多语言人才和50-70%的成本节约。`,
  kpiTitle: (c) => `${c}客户的保障性能`,
  kpiSubtitle: (c) => `我们以合同形式保证KPI，为${c}市场量身定制SLA。`,
  pricingTitle: (c) => `成本比较：${c} vs 摩洛哥`,
  industriesTitle: (c) => `${c}服务行业`,
  industriesSubtitle: (c) => `我们深耕${c}经济核心行业。`,
  languagesTitle: (c) => `${c}市场语言`,
  languagesSubtitle: (c) => `我们的座席是母语使用者。涵盖${c}客户期望的所有语言。`,
  complianceTitle: (c) => `${c}合规要求`,
  complianceSubtitle: (c) => `所有${c}运营均符合国际数据保护标准。`,
  timelineTitle: (c) => `${c}项目上线时间表`,
  timelineSubtitle: '从签约到上线仅需2-4周。',
  faqTitle: (c) => `常见问题 — ${c}呼叫中心`,
  relatedTitle: '相关资源',
  ctaTitle: (c) => `准备好外包${c}客户支持了吗？`,
  ctaSubtitle: (c) => `获取为${c}市场定制的方案。免费咨询，无需承诺。`,
  ctaButton: '申请免费咨询',
  learnMoreMorocco: '了解摩洛哥作为BPO目的地的更多信息',
  exploreIndustries: '探索所有行业',
  viewPricing: '查看详细价格',
};

const ja: CountryTemplateLabels = {
  servicesTitle: (c) => `${c}向けコールセンターサービス`,
  servicesSubtitle: (c, l) => `${c}専属チームが全チャネルで包括的なカスタマーサポートを提供。${l}ネイティブスピーカー対応。`,
  whyMoroccoTitle: (c) => `なぜ${c}からモロッコにアウトソーシング？`,
  whyMoroccoSubtitle: (c) => `モロッコは${c}企業に比類なきニアショア優位性を提供：文化的近接性、タイムゾーン整合、多言語人材、50-70%のコスト削減。`,
  kpiTitle: (c) => `${c}クライアントへの保証パフォーマンス`,
  kpiSubtitle: (c) => `${c}市場に適応したSLAでKPIを契約保証。`,
  pricingTitle: (c) => `コスト比較：${c} vs モロッコ`,
  industriesTitle: (c) => `${c}で対応する業界`,
  industriesSubtitle: (c) => `${c}経済を牽引する業界に深い専門知識。`,
  languagesTitle: (c) => `${c}市場対応言語`,
  languagesSubtitle: (c) => `エージェントはネイティブスピーカー。${c}のお客様が期待する全言語に対応。`,
  complianceTitle: (c) => `${c}コンプライアンス`,
  complianceSubtitle: (c) => `${c}向け全業務は国際データ保護基準に準拠。`,
  timelineTitle: (c) => `${c}プロジェクト稼働タイムライン`,
  timelineSubtitle: '契約から稼働まで最短2-4週間。',
  faqTitle: (c) => `よくある質問 — ${c}コールセンター`,
  relatedTitle: '関連リソース',
  ctaTitle: (c) => `${c}のカスタマーサポートをアウトソースする準備はできましたか？`,
  ctaSubtitle: (c) => `${c}市場に合わせたカスタム提案を取得。無料相談、コミットメント不要。`,
  ctaButton: '無料相談をリクエスト',
  learnMoreMorocco: 'BPO先としてのモロッコについて詳しく',
  exploreIndustries: 'すべての業界を見る',
  viewPricing: '詳細な価格を見る',
};

const ko: CountryTemplateLabels = {
  servicesTitle: (c) => `${c} 콜센터 서비스`,
  servicesSubtitle: (c, l) => `${c} 전담 팀이 모든 채널에서 포괄적인 고객 지원을 제공합니다. ${l} 원어민 상담원.`,
  whyMoroccoTitle: (c) => `왜 ${c}에서 모로코로 아웃소싱해야 할까요?`,
  whyMoroccoSubtitle: (c) => `모로코는 ${c} 기업에 비교할 수 없는 니어쇼어 이점을 제공합니다: 문화적 근접성, 시간대 일치, 다국어 인재, 50-70% 비용 절감.`,
  kpiTitle: (c) => `${c} 고객을 위한 보장된 성과`,
  kpiSubtitle: (c) => `${c} 시장에 맞춤화된 SLA로 KPI를 계약적으로 보장합니다.`,
  pricingTitle: (c) => `비용 비교: ${c} vs 모로코`,
  industriesTitle: (c) => `${c}에서 서비스하는 산업`,
  industriesSubtitle: (c) => `${c} 경제를 이끄는 산업에 대한 깊은 전문성을 보유하고 있습니다.`,
  languagesTitle: (c) => `${c} 시장 언어`,
  languagesSubtitle: (c) => `상담원은 원어민입니다. ${c} 고객이 기대하는 모든 언어를 지원합니다.`,
  complianceTitle: (c) => `${c} 컴플라이언스`,
  complianceSubtitle: (c) => `${c}의 모든 운영은 국제 데이터 보호 표준을 준수합니다.`,
  timelineTitle: (c) => `${c} 프로젝트 가동 일정`,
  timelineSubtitle: '계약에서 가동까지 2-4주.',
  faqTitle: (c) => `자주 묻는 질문 — ${c} 콜센터`,
  relatedTitle: '관련 리소스',
  ctaTitle: (c) => `${c} 고객 지원을 아웃소싱할 준비가 되셨나요?`,
  ctaSubtitle: (c) => `${c} 시장에 맞춤화된 제안을 받으세요. 무료 상담.`,
  ctaButton: '무료 상담 요청',
  learnMoreMorocco: 'BPO 목적지로서의 모로코에 대해 더 알아보기',
  exploreIndustries: '모든 산업 탐색',
  viewPricing: '상세 가격 보기',
};

const LABELS_MAP: Record<string, CountryTemplateLabels> = {
  en, fr, es, ar, de, it, pt, nl, pl, tr, sv, da, ru, zh, ja, ko,
};

export function getCountryTemplateLabels(lang: string): CountryTemplateLabels {
  return LABELS_MAP[lang] || en;
}
